import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  useTable, useSortBy, useExpanded, usePagination,
} from 'react-table';
import Pagination from './pagination/Pagination';
import './data-table.css';

const DEFAULT_PAGE_ROWS_COUNT = 5;
const DEFAULT_MAX_PAGES_ROWS_COUNT = 25;
const LOADING_TEXT = 'Cargando datos...';
const NO_DATA_TEXT = 'No hay datos que mostrar';

/**
 * Translates the project's v6-shaped column definitions
 * (`{ Header, accessor, sortable, Cell: row => ... }`, where `Cell` reads
 * only `row.original`) into react-table v7 column defs, without requiring
 * any of the 9 DataTable consumers to change their column functions.
 * See design D1 (sdd/upgrade-react-17-peer-deps).
 *
 * v6 identified columns positionally, so two distinct columns were allowed to
 * declare the same accessor. v7 requires unique column ids and throws
 * ("Duplicate columns were found with ids") when they collide. Consumers build
 * their column arrays conditionally at render time, so collisions only appear
 * on some tabs. To preserve v6 behavior, the first occurrence keeps the
 * accessor as its id and later duplicates get a suffixed id, while `accessor`
 * stays intact so value lookup and the server-side sort key are unaffected.
 */
function toV7Columns(columns) {
  const usedIds = new Set();

  return columns.map(({ sortable, Cell, ...rest }, index) => {
    const preferredId = rest.id || (typeof rest.accessor === 'string' ? rest.accessor : `column-${index}`);
    const id = usedIds.has(preferredId) ? `${preferredId}__${index}` : preferredId;
    usedIds.add(id);

    return {
      ...rest,
      id,
      disableSortBy: !sortable,
      ...(Cell && {
        Cell: (cellProps) => Cell({
          original: cellProps.row.original,
          index: cellProps.row.index,
          value: cellProps.row.values[id],
          row: cellProps.row.values,
        }),
      }),
    };
  });
}

/**
 * Maps each v7 column id back to the key the server expects for sorting, which
 * is the original accessor. They differ only for de-duplicated columns.
 */
function toSortKeysById(v7Columns) {
  return v7Columns.reduce((keys, column) => {
    keys[column.id] = typeof column.accessor === 'string' ? column.accessor : column.id;
    return keys;
  }, {});
}

const DataTable = forwardRef((props, ref) => {
  const {
    columns,
    fetchDataCallback,
    maxPagesRowsCount = DEFAULT_MAX_PAGES_ROWS_COUNT,
    SubComponent,
    isDefaultExpanded,
    notShowLoading,
  } = props;

  const v7Columns = useMemo(() => toV7Columns(columns), [columns]);
  const sortKeysById = useMemo(() => toSortKeysById(v7Columns), [v7Columns]);
  const [data, setData] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const hasLoadedDataRef = useRef(false);

  const initialState = useMemo(() => ({ pageIndex: 0, pageSize: maxPagesRowsCount }), [maxPagesRowsCount]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    toggleRowExpanded,
    gotoPage,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns: v7Columns,
      data,
      initialState,
      manualPagination: true,
      manualSortBy: true,
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetExpanded: false,
      pageCount,
    },
    useSortBy,
    useExpanded,
    usePagination,
  );

  const extractPagesFromPageData = useCallback((extra) => {
    const recordsTotal = extra.recordsTotal || extra.recordsFiltered;
    return Math.ceil(recordsTotal / maxPagesRowsCount);
  }, [maxPagesRowsCount]);

  useEffect(() => {
    if (!fetchDataCallback) {
      return;
    }

    const sorted = sortBy.reduce((acc, column) => {
      acc[sortKeysById[column.id] || column.id] = column.desc;
      return acc;
    }, {});

    const params = {
      per_page: pageSize,
      page: pageIndex + 1,
      sorted: JSON.stringify(sorted),
    };

    if (typeof notShowLoading === 'undefined' || !hasLoadedDataRef.current) {
      setLoading(true);
    }

    fetchDataCallback(params).then((response) => {
      hasLoadedDataRef.current = true;
      setData(response.data);
      setPageCount(extractPagesFromPageData(response.extra));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, sortBy, reloadTick]);

  useEffect(() => {
    if (typeof isDefaultExpanded === 'function') {
      data.forEach((rawRow, index) => {
        if (isDefaultExpanded(rawRow)) {
          toggleRowExpanded(String(index), true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useImperativeHandle(ref, () => ({
    reloadData: () => setReloadTick((tick) => tick + 1),
  }));

  const fillerRowsCount = Math.max(0, DEFAULT_PAGE_ROWS_COUNT - page.length);

  return (
    <div>
      <div className="ReactTable -striped -highlight pro-data-table">
        <div className="rt-table" {...getTableProps()}>
          <div className="rt-thead -header">
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <div className="rt-tr" key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const sortClass = column.isSorted ? (column.isSortedDesc ? '-sort-desc' : '-sort-asc') : '';
                    const cursorClass = column.canSort ? '-cursor-pointer' : '';
                    const headerProps = column.getHeaderProps(column.canSort ? column.getSortByToggleProps() : undefined);
                    const { key: columnKey, ...columnProps } = headerProps;
                    return (
                      <div className={`rt-th ${sortClass} ${cursorClass}`.trim()} key={columnKey} {...columnProps}>
                        {column.render('Header')}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <div className="rt-tbody" {...getTableBodyProps()}>
            {!loading && page.length === 0 && (
              <div className="rt-noData">{NO_DATA_TEXT}</div>
            )}
            {page.map((row, rowIndex) => {
              prepareRow(row);
              const { key: rowKey, ...rowProps } = row.getRowProps();
              return (
                <div className="rt-tr-group" key={rowKey}>
                  <div className={`rt-tr ${rowIndex % 2 !== 0 ? '-odd' : '-even'}`} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps();
                      return (
                        <div className="rt-td" key={cellKey} {...cellProps}>
                          {cell.render('Cell')}
                        </div>
                      );
                    })}
                  </div>
                  {typeof SubComponent === 'function' && row.isExpanded && (
                    <div className="rt-tr rt-expanded-subcomponent">
                      {SubComponent({ original: row.original, index: row.index, row: row.values })}
                    </div>
                  )}
                </div>
              );
            })}
            {/* S6479: filler rows are pure layout padding — no domain entity, no reordering,
                no attached state. The array index IS the identity here. */}
            {Array.from({ length: fillerRowsCount }).map((_unused, fillerIndex) => (
              <div
                className="rt-tr-group"
                // eslint-disable-next-line react/no-array-index-key
                key={/* NOSONAR */ `pad-row-${fillerIndex}`}
              >
                <div className="rt-tr -padRow">
                  <div className="rt-td">&nbsp;</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="-loading -active">
                <div>{LOADING_TEXT}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="pagination-bottom">
        <Pagination pages={pageCount} page={pageIndex} onPageChange={gotoPage} />
      </div>
    </div>
  );
});

DataTable.propTypes = {
  columns: PropTypes.array,
  fetchDataCallback: PropTypes.func,
  maxPagesRowsCount: PropTypes.number,
  SubComponent: PropTypes.func,
  isDefaultExpanded: PropTypes.func,
  notShowLoading: PropTypes.bool,
};

export default DataTable;
